import { Button, Icon, IconButton, InputField, Link, Typography } from '@branchjs/neo-components';
import type { ChangeEvent, FormEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link as ReactRouterLink } from 'react-router-dom';
import LoggedoutActions from '../../../main/actions/LoggedoutActions';
import LoggedoutStore from '../../../main/stores/LoggedoutStore';
import { emailValid as _emailValid, inputTooLong } from '../ScreenSignupValidation';
import LoggedOutContainer from '../components/LoggedOutContainer/LoggedOutContainer';
import { useStyles } from './ScreenLogin.styles';
import type { ErrorFootnotes } from './ScreenLogin.types';
import useAuthenticateLogin from './useAuthenticateLogin';

const ScreenLogin = () => {
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [errors, setErrors] = useState<any>(LoggedoutStore.getErrors());
  const [loadingSignIn, setLoadingSignIn] = useState<boolean>(false);
  const [emailValid, setEmailValid] = useState<boolean>(false);
  const [passwordValid, setPasswordValid] = useState<boolean>(false);
  const [errorFootnotes, setErrorFootnotes] = useState<ErrorFootnotes>({ email: null, password: null });
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const { isOAuthFlowEnabled, isOAuthFlowNotEnabledForAccount, handleAuthenticate, setIsOAuthFlowNotEnabledForAccount } =
    useAuthenticateLogin();

  const formRef = useRef<HTMLFormElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();

  const _preventDefault = (event: Event | FormEvent) => event.preventDefault();

  const _validateEmail = (input: string | null) => {
    const valid = _emailValid(input);
    setEmailValid(valid);
    setErrorFootnotes((prevErrorFootnotes) => ({
      ...prevErrorFootnotes,
      email: !valid
        ? 'Please specify a valid email address e.g., myname@email.com.'
        : inputTooLong(input)
          ? 'Please specify a shorter email.'
          : '',
    }));
    return valid;
  };

  const _handleEmailChange = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = ev.target.value;
    setEmail(input);
    _validateEmail(input);
    localStorage.setItem('reset_password_user_email', input);
    LoggedoutActions.clearErrors();
  };

  const _validatePassword = (input: string | null) => {
    const valid = !!input;
    setErrorFootnotes((prevErrorFootnotes) => ({
      ...prevErrorFootnotes,
      password: !input ? 'Please specify a password.' : '',
    }));
    setPasswordValid(!!input);
    return valid;
  };

  const _handlePasswordChange = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = ev.target.value;
    setPassword(input);
    _validatePassword(input);
    LoggedoutActions.clearErrors();
  };

  const passwordRequired = !isOAuthFlowEnabled || isOAuthFlowNotEnabledForAccount;

  const handleStandardLogin = async (emailId: string | null, passwordValue: string | null) => {
    const queryParams = new URLSearchParams(location.search);
    const nextPath = queryParams.get('next_path') || false;
    // @ts-ignore - nextPath handling if missing or undefined
    await LoggedoutActions.login(emailId, passwordValue, nextPath);
  };

  const _login = async () => {
    let currentEmail = email;

    // Handle Ugly Safari Autofill -- https://github.com/facebook/react/issues/1159
    if (!currentEmail && emailInputRef.current) {
      currentEmail = emailInputRef.current.value;
      setEmail(currentEmail);
    }

    let currentPassword = password;
    if (!currentPassword && passwordInputRef.current) {
      currentPassword = passwordInputRef.current.value;
      setPassword(currentPassword);
    }

    const isEmailValid = _validateEmail(currentEmail);
    const isPasswordValid = passwordRequired ? _validatePassword(currentPassword) : true;

    if (!isEmailValid || (passwordRequired && !isPasswordValid)) {
      return;
    }

    setLoadingSignIn(true);

    if (isOAuthFlowEnabled && !isOAuthFlowNotEnabledForAccount && currentEmail) {
      handleAuthenticate(currentEmail);
      setLoadingSignIn(false);
    } else {
      await handleStandardLogin(currentEmail, currentPassword);
    }
  };

  const onCancel = () => {
    setIsOAuthFlowNotEnabledForAccount(false);
    setEmail('');
    setPassword('');
    setEmailValid(false);
    setPasswordValid(false);
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      _login();
    }
  };

  const handleChange = () => {
    const externalErrors = LoggedoutStore.getErrors();
    setErrors(externalErrors);
    if (externalErrors.login_attempts) {
      return history.push('/reset-password');
    }
    if (externalErrors && Object.keys(externalErrors).length !== 0) {
      setLoadingSignIn(false);
    }
    if (externalErrors.email_verification) {
      localStorage.setItem('accept_invite_user_email', email ?? '');
      history.push('/accept-email-invite');
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  useEffect(() => {
    /* componentWillMount */
    window.addEventListener('keypress', handleKeyPress);
    LoggedoutStore.addChangeListener(handleChange);

    /* componentDidMount */
    LoggedoutActions.clearErrors();
    if (formRef.current) {
      formRef.current.addEventListener('invalid', _preventDefault, true);
    }

    return () => {
      window.removeEventListener('keypress', handleKeyPress);
      LoggedoutStore.removeChangeListener(handleChange);
      if (formRef.current) {
        formRef.current.removeEventListener('invalid', _preventDefault, true);
      }
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  }, [handleKeyPress, handleChange]);

  const _renderErrors = (key: string | number) => {
    return errorFootnotes[key] ? errorFootnotes[key] : errors[key];
  };

  return (
    <LoggedOutContainer title="Sign in" linkTitle="New user?" linkText="Create an account" linkPathName="/">
      <form role="form" id="form_login" ref={formRef} onSubmit={_preventDefault}>
        <div className={classes.workEmail}>
          <InputField
            id="email"
            type="text"
            title="Work email"
            name="email"
            onChange={_handleEmailChange}
            value={email ?? ''}
            inputRef={emailInputRef}
            placeholder="Enter your work email"
            error={_renderErrors('email') || _renderErrors('account_blocked')}
            testid="work-email-input-input"
            success={emailValid}
            fullWidth
            disabled={isOAuthFlowEnabled && isOAuthFlowNotEnabledForAccount}
          />
        </div>
        {passwordRequired && (
          <div className={classes.password}>
            <InputField
              id="password-input"
              type={passwordVisible ? 'text' : 'password'}
              title="Password"
              name="password"
              onChange={_handlePasswordChange}
              value={password ?? ''}
              inputRef={passwordInputRef}
              testid="password-input-input"
              placeholder="Enter your password"
              error={_renderErrors('password')}
              success={passwordValid}
              // displayShowHide={true}
              // pasteDisabled={true}
              autoComplete="current-password"
              fullWidth
              customTitleEndAdornment={
                <IconButton
                  id="showHidePasswordButton"
                  size="small"
                  color={passwordVisible ? 'primary' : 'secondary'}
                  edge="end"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                >
                  <Icon id="showHidePasswordIcon">{passwordVisible ? 'visibility' : 'visibility_off'}</Icon>
                  <Typography variant="body2" color="primary" className={classes.showHidePasswordLink}>
                    {passwordVisible ? 'Hide' : 'Show'}
                  </Typography>
                </IconButton>
              }
            />
          </div>
        )}
        {passwordRequired && (
          <div className={classes.forgotPassword}>
            <Link id="forgot-password" component={ReactRouterLink} to={{ pathname: '/reset-password', search: '' }}>
              Forgot password?
            </Link>
          </div>
        )}

        <div className={classes.signIn}>
          {isOAuthFlowNotEnabledForAccount && (
            <Button
              variant="outlined"
              onClick={onCancel}
              id="btn-cancel-sign-in"
              disabled={loadingSignIn}
              className={classes.buttonSignIn}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={_login}
            testid="btn-sign-in"
            loading={loadingSignIn}
            className={classes.buttonSignIn}
          >
            {passwordRequired ? 'Sign in' : 'Authenticate'}
          </Button>
        </div>
      </form>
      <div className={classes.footer}>
        <div>
          <span>Questions? We are here to </span>
          <Link href="https://support.branch.io" id="help-support-link">
            help
          </Link>
          <span>.</span>
        </div>
      </div>
    </LoggedOutContainer>
  );
};

export default ScreenLogin;
